<template>
  <div class="base-date-navigator" :class="sizeClass">
    <button type="button" class="date-navigator__btn" :disabled="disabled" @click="step(-1)" :aria-label="'Previous ' + mode">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <base-input class="base-date-navigator__picker mb-0" :style="pickerStyle">
      <el-date-picker
        :editable="false"
        :clearable="false"
        :disabled="disabled"
        :type="mode === 'month' ? 'month' : 'date'"
        :size="pickerSize"
        placeholder="Select date"
        value-format="yyyy-MM-dd"
        :format="resolvedFormat"
        :value="value"
        :picker-options="pickerOptions"
        @input="onPickerInput"
      />
    </base-input>
    <button type="button" class="date-navigator__btn" :disabled="disabled" @click="step(1)" :aria-label="'Next ' + mode">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>
    <button
      v-if="showToday"
      type="button"
      class="date-navigator__today-btn"
      :disabled="disabled"
      @click="goToToday"
    >Today</button>
    <!-- Trailing actions scoped to the shown day (for example a download of
         everything on it). Consumers style their button with
         .date-navigator__action-btn so it matches Today by construction. -->
    <div v-if="$slots.actions" class="base-date-navigator__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<script>
import { pickerDateFormat } from '@/plugins/dates'
import { fitDateInputWidth } from '@/util/dateInputWidth'

function readGroup(){
  try {
    return JSON.parse(localStorage.getItem('group')) || {}
  } catch(e){
    return {}
  }
}

function today(){
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function shift(iso, days){
  if(!iso) return today()
  const d = new Date(iso + 'T00:00:00')
  if(isNaN(d.getTime())) return today()
  d.setDate(d.getDate() + days)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export default {
  name: 'base-date-navigator',
  props: {
    value: { type: String, default: '' },
    showToday: { type: Boolean, default: true },
    size: {
      type: String,
      default: 'default',
      validator: v => ['default', 'small', 'sm'].includes(v)
    },
    format: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // Size the date box to the widest date its format can show (weekday prefix, long month
    // names) instead of stretching to the space available. For tight toolbars.
    fitContent: { type: Boolean, default: false },
    // day: arrows step one day; week: seven days; month: to the 1st of the adjacent month
    mode: {
      type: String,
      default: 'day',
      validator: v => ['day', 'week', 'month'].includes(v)
    }
  },
  data(){
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      fitWidth: null
    }
  },
  mounted(){
    this._onResize = () => { this.windowWidth = window.innerWidth }
    window.addEventListener('resize', this._onResize)
    this.$nextTick(this.measureFit)
    // The first measure can run before the web font loads; measure again once it has
    if(this.fitContent && document.fonts && document.fonts.ready){
      document.fonts.ready.then(() => { if(!this._isDestroyed) this.measureFit() })
    }
  },
  watch: {
    resolvedFormat(){ this.$nextTick(this.measureFit) },
    pickerSize(){ this.$nextTick(this.measureFit) }
  },
  beforeDestroy(){
    if(this._onResize) window.removeEventListener('resize', this._onResize)
  },
  computed: {
    isMobileViewport(){
      return this.windowWidth < 576
    },
    sizeClass(){
      return this.size === 'small' || this.size === 'sm' ? 'is-sm' : ''
    },
    pickerSize(){
      return (this.size === 'small' || this.size === 'sm' || this.isMobileViewport) ? 'small' : ''
    },
    group(){
      return readGroup()
    },
    resolvedFormat(){
      if(this.format) return this.format
      if(this.mode === 'month') return 'MMMM yyyy'
      const base = pickerDateFormat(this.group.date_format)
      const isCompact = this.size === 'small' || this.size === 'sm' || this.isMobileViewport
      return (this.group.show_day_of_week && !isCompact) ? `ddd, ${base}` : base
    },
    pickerOptions(){
      return {
        firstDayOfWeek: this.group.first_day_of_week || 7
      }
    },
    pickerStyle(){
      if(!this.fitContent || !this.fitWidth) return null
      return { flex: '0 0 auto', width: this.fitWidth + 'px', minWidth: '0' }
    }
  },
  methods: {
    measureFit(){
      if(!this.fitContent || !this.$el || !this.$el.querySelector) return
      this.fitWidth = fitDateInputWidth(this.$el.querySelector('.el-input__inner'), this.resolvedFormat)
    },
    emitChange(next){
      this.$emit('input', next)
      this.$emit('change', next)
    },
    onPickerInput(val){
      this.emitChange(val || '')
    },
    step(direction){
      if(this.mode === 'month'){
        const d = new Date((this.value || today()) + 'T00:00:00')
        const target = new Date(d.getFullYear(), d.getMonth() + direction, 1)
        this.emitChange(`${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-01`)
        return
      }
      this.emitChange(shift(this.value, this.mode === 'week' ? 7 * direction : direction))
    },
    shiftDay(days){
      this.emitChange(shift(this.value, days))
    },
    goToToday(){
      this.emitChange(today())
    }
  }
}
</script>

<style lang="scss" scoped>
.base-date-navigator {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.base-date-navigator__picker {
  flex: 1 1 auto;
  min-width: 140px;

  ::v-deep .el-input {
    width: 100%;
  }
}

@mixin compact-navigator {
  gap: 2px;

  .base-date-navigator__picker {
    min-width: 110px;
  }

  ::v-deep .el-input__inner {
    height: 32px;
    line-height: 32px;
    padding-left: 28px;
    padding-right: 8px;
    font-size: 12px;
  }

  ::v-deep .el-input__prefix {
    left: 4px;
  }

  ::v-deep .el-input__icon {
    line-height: 32px;
  }
}

.base-date-navigator.is-sm {
  @include compact-navigator;
}

@media (max-width: 575.98px) {
  .base-date-navigator {
    @include compact-navigator;
  }
}

.date-navigator__btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
}

@mixin compact-nav-btn {
  width: 32px;
  height: 32px;

  svg {
    width: 14px;
    height: 14px;
  }
}

.base-date-navigator.is-sm .date-navigator__btn {
  @include compact-nav-btn;
}

@media (max-width: 575.98px) {
  .base-date-navigator .date-navigator__btn {
    @include compact-nav-btn;
  }
}

.date-navigator__today-btn {
  min-height: 40px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.15);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-left: 4px;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: rgba(225, 78, 202, 0.25);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

@mixin compact-today-btn {
  min-height: 32px;
  padding: 4px 10px;
  font-size: 11px;
  margin-left: 2px;
}

.base-date-navigator.is-sm .date-navigator__today-btn {
  @include compact-today-btn;
}

@media (max-width: 575.98px) {
  .base-date-navigator .date-navigator__today-btn {
    @include compact-today-btn;
  }
}
</style>

<style lang="scss">
// Trailing action button. Unscoped because slot content carries the consumer's
// scope id, not this component's. Values mirror .date-navigator__today-btn
// and compact-today-btn above so the two never drift apart.
.base-date-navigator .base-date-navigator__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  flex-shrink: 0;
}
.base-date-navigator .date-navigator__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;
  min-width: 40px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.15);
  border: none;
  border-radius: 6px;
  cursor: pointer;

  i { font-size: 15px; line-height: 1; }

  &:hover:not(:disabled) { background: rgba(225, 78, 202, 0.25); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.4); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}
.base-date-navigator.is-sm .base-date-navigator__actions { margin-left: 2px; }
.base-date-navigator.is-sm .date-navigator__action-btn {
  min-height: 32px;
  min-width: 32px;
  padding: 4px 8px;
  font-size: 11px;
  i { font-size: 13px; }
}
@media (max-width: 575.98px) {
  .base-date-navigator .base-date-navigator__actions { margin-left: 2px; }
  .base-date-navigator .date-navigator__action-btn {
    min-height: 32px;
    min-width: 32px;
    padding: 4px 8px;
    font-size: 11px;
    i { font-size: 13px; }
  }
}
.white-content .base-date-navigator .date-navigator__action-btn {
  color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
  &:hover:not(:disabled) { background: rgba(225, 78, 202, 0.2); }
}

// Light-mode overrides (unscoped so .white-content parent selector works)
.white-content .base-date-navigator {
  .date-navigator__btn {
    color: rgba(0, 0, 0, 0.4);

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.05);
      color: #344675;
    }
  }

  .date-navigator__today-btn {
    color: #e14eca;
    background: rgba(225, 78, 202, 0.1);

    &:hover:not(:disabled) {
      background: rgba(225, 78, 202, 0.2);
    }
  }
}
</style>
